"use client";

// useLiveConnections — the minted connections, kept true while you look at them.
//
// A connection's state is not stored; it is DERIVED from `last_seen_at`, which
// the MCP route stamps on every single call (`connection_touch`). That makes the
// underlying data perfectly live and the screen showing it perfectly stale: the
// panel fetched once on mount, so a tool could connect, read your whole memory
// and go quiet again while the page still said "waiting for its first call".
// The only way to see the truth was to reload — which is exactly the moment a
// person decides the connection didn't work and goes to mint another one.
//
// Under the stateless MCP (2026-07-28) there is no session to subscribe to, so
// the connection row IS the presence signal. Three things keep it honest:
//
//   1. REALTIME — a postgres_changes stream on `connections` for this user. A
//      call lands, `last_seen_at` moves, the row arrives, the dot lights. This
//      is the fast path and usually the only one that does anything.
//   2. A VISIBLE-TAB POLL — the safety net. Realtime can be unavailable (the
//      table not in the publication yet, a websocket the network ate, a tab
//      restored from bfcache). Polling only while the document is visible means
//      a backgrounded dashboard costs nothing, and returning to the tab
//      refreshes immediately rather than on the next tick. While the stream is
//      SUBSCRIBED the poll steps back to once every few minutes: every open
//      dashboard tab polling every 20s was the busiest function on the server
//      (~2.5k calls a day, 2026-09-30) for a list realtime was already keeping.
//   3. A TICKING CLOCK — `live` decays with time and nothing else. Without a
//      tick, a connection that went quiet an hour ago would still be lit,
//      because no data changed to re-render it.

import { useCallback, useEffect, useRef, useState } from "react";
import { createClient } from "../supabase/client";
import { startPoll } from "../os/poll";
import { LIVE_WINDOW_MS, type Connection } from "./connections";

/** how often the safety-net poll runs while the tab is visible and realtime is down. */
const POLL_MS = 20_000;
/** how often it still runs while realtime is streaming — a net under the net. */
const STREAMING_POLL_MS = 5 * 60_000;
/** how often `now` advances — fine enough that `live` expires on time. */
const TICK_MS = Math.min(30_000, LIVE_WINDOW_MS / 3);

export function useLiveConnections(): {
  connections: Connection[];
  /** re-read now (after minting, renaming, letting one go). */
  reload: () => Promise<void>;
  /** a ticking clock to pass to `connState`, so states decay on their own. */
  now: number;
  /** has the list answered at least once? (an empty list before then means "not read yet", never "none") */
  loaded: boolean;
} {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  // is the realtime stream up? while it is, the poll mostly stands down.
  const streaming = useRef(false);
  const lastLoad = useRef(0);

  // says whether the read worked, so the safety-net poll can back off a server
  // that is struggling instead of asking it again at full pace
  const read = useCallback(async (): Promise<boolean> => {
    lastLoad.current = Date.now();
    try {
      const res = await fetch("/api/connections", { cache: "no-store" });
      if (!res.ok) return false;
      const data = (await res.json()) as { connections?: Connection[] };
      if (Array.isArray(data.connections)) {
        setConnections(data.connections);
        setLoaded(true);
      }
      setNow(Date.now());
      return true;
    } catch {
      /* offline — whatever we last knew is better than an empty list */
      return false;
    }
  }, []);
  const reload = useCallback(async () => {
    await read();
  }, [read]);

  useEffect(() => { void reload(); }, [reload]);

  // the clock. Also re-reads the moment the tab comes back, because time passed
  // that we deliberately did not spend polling.
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), TICK_MS);
    const onVis = () => { if (!document.hidden) void reload(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(tick); document.removeEventListener("visibilitychange", onVis); };
  }, [reload]);

  // the safety-net poll: silent while the tab is hidden or offline, backing off
  // a failing server up to a minute (lib/os/poll), and standing down to once
  // every few minutes while the realtime stream is up.
  useEffect(
    () =>
      startPoll({
        every: POLL_MS,
        run: async () => {
          if (streaming.current && Date.now() - lastLoad.current < STREAMING_POLL_MS) return true;
          return read();
        },
      }),
    [read],
  );

  // the fast path.
  useEffect(() => {
    let disposed = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let channel: any = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let supabase: any = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // a burst of calls is one conversation, not twenty repaints.
    const onChange = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void reload(), 250);
    };

    (async () => {
      try {
        supabase = createClient();
        const { data: u } = await supabase.auth.getUser();
        const uid = u.user?.id;
        if (disposed || !uid) return;
        channel = supabase
          .channel(`conns-${uid}`)
          .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "connections", filter: `user_id=eq.${uid}` },
            onChange,
          )
          .subscribe((status: string) => {
            // SUBSCRIBED → the fast path is live; anything else (CHANNEL_ERROR,
            // TIMED_OUT, CLOSED) → the poll takes the watch back.
            streaming.current = status === "SUBSCRIBED";
          });
      } catch {
        /* no realtime here — the poll above is why this is only the fast path */
      }
    })();

    return () => {
      disposed = true;
      streaming.current = false;
      if (timer) clearTimeout(timer);
      if (channel && supabase) { try { supabase.removeChannel(channel); } catch { /* ignore */ } }
    };
  }, [reload]);

  return { connections, reload, now, loaded };
}
